/* eslint-disable consistent-return */

import DateRangeHandler from '@common/utils/DateRangeHandler';

import { mappings } from './index';

const USER_TENANT = 'tenant';

export const CustomDimensionsManager = instance => ({
  setCustomDimensions(cd) {
    try {
      instance.setCustomDimension(cd);
      if (cd.pageLanguage) {
        instance.set({ locale: cd.pageLanguage });
      }
    } catch (err) {
      console.error('Failed to call setCustomDimensions', { err });
    }
  },
  setCurrencyCode() {
    return code => {
      try {
        instance.ecommerceManager.setCurrencyCode(code);
      } catch (err) {
        console.error('Failed to call setCurrencyCode', {
          err
        });
      }
    };
  },
  clearListingCustomDimensions() {
    try {
      const clearedCd = {
        propertyType: undefined,
        propertyRoomsNumber: undefined,
        propertyHouseRules: undefined,
        propertyContractType: undefined,
        propertyCancellationType: undefined,
        propertyBillsIncluded: undefined,
        propertyMinimumStay: undefined,
        propertyChecked: undefined,
        propertyFeatures: undefined,
        propertySliderElementsOrder: undefined
      };
      this.setCustomDimensions(clearedCd);
    } catch (err) {
      console.error('Failed to clearListingCustomDimensions', { err });
    }
  },
  setListingCustomDimensions({
    listing,
    isLogged,
    locale,
    pageType,
    moveIn,
    moveOut
  }) {
    try {
      const cd = {
        userType: USER_TENANT,
        userRegistered: isLogged.toString(),
        pageType,
        pageLanguage: locale,
        propertyCity: listing.cityId,
        propertyType: listing.type,
        propertyRoomsNumber: Array.isArray(listing.roomsInApartment)
          ? listing.roomsInApartment.length
          : undefined,
        propertyHouseRules: mappings.mapHouseRules(listing.landlordRules),
        propertyContractType: listing.contractType,
        propertyCancellationType: listing.cancellationPolicy,
        propertyBillsIncluded: mappings.mapBillsIncluded(listing.bills),
        propertyMinimumStay: listing.minStay,
        propertyChecked: listing.checked.toString(),
        propertyFeatures: mappings.mapHouseFeatures({
          ...listing.features,
          ...listing.extraFeatures
        })
      };
      if (moveIn && moveOut) {
        cd.moveInDate = moveIn;
        cd.moveOutDate = moveOut;
        cd.numNights = DateRangeHandler.getDaysdiff(moveIn, moveOut);
        cd.daysInAdvance = DateRangeHandler.getDaysdiff(Date.now(), moveIn);
        cd.propertyAvailable = DateRangeHandler.checkIfAvailable(
          moveIn,
          moveOut,
          listing.unavailabilities
        ).toString();
      }
      this.setCustomDimensions(cd);
    } catch (err) {
      console.error('Failed to get common custom dimensions', { err });
    }
  },
  setBookNowButtonCustomDimensions({
    type,
    moveIn,
    moveOut,
    pricing,
    cityId,
    roomsInApartment,
    landlordRules,
    contractType,
    cancellationPolicy,
    bills,
    minStay,
    checked,
    features,
    extraFeatures,
    unavailabilities
  }) {
    try {
      const customDimensions = {
        moveInDate: moveIn,
        moveOutDate: moveOut,
        reservationFee: pricing.tenantFeeGross,
        contractValue: pricing.totalContractValue,
        numNights: DateRangeHandler.getDaysdiff(moveIn, moveOut),
        daysInAdvance: DateRangeHandler.getDaysdiff(Date.now(), moveIn),
        propertyAvailable: DateRangeHandler.checkIfAvailable(
          moveIn,
          moveOut,
          unavailabilities
        ).toString(),
        propertyCity: cityId,
        propertyType: type,
        propertyRoomsNumber: Array.isArray(roomsInApartment)
          ? roomsInApartment.length
          : undefined,
        propertyHouseRules: mappings.mapHouseRules(landlordRules),
        propertyContractType: contractType,
        propertyCancellationType: cancellationPolicy,
        propertyBillsIncluded: mappings.mapBillsIncluded(bills),
        propertyMinimumStay: minStay,
        propertyChecked: checked.toString(),
        propertyFeatures: mappings.mapHouseFeatures({
          ...features,
          ...extraFeatures
        })
      };
      this.setCustomDimensions(customDimensions);
    } catch (err) {
      console.error('Failed to set listing selected dates custom dimensions', {
        err
      });
    }
  },
  setSearchCommonCustomDimensions({
    city,
    moveIn,
    moveOut,
    locale,
    isLogged,
    pageType
  }) {
    try {
      const customDimensions = {
        userType: USER_TENANT,
        userRegistered: isLogged.toString(),
        pageType,
        pageLanguage: locale
      };
      if (city) {
        customDimensions.propertyCity = city;
      }
      if (moveIn && moveOut) {
        customDimensions.moveInDate = moveIn;
        customDimensions.moveOutDate = moveOut;
        customDimensions.numNights = DateRangeHandler.getDaysdiff(
          moveIn,
          moveOut
        );
        customDimensions.daysInAdvance = DateRangeHandler.getDaysdiff(
          Date.now(),
          moveIn
        );
      }
      this.setCustomDimensions(customDimensions);
    } catch (err) {
      console.error('Failed to set custom dimensions', { err });
    }
  },
  specialFeaturesFromFilters(filters) {
    const specialFeatures = [];
    if (filters.noDeposit === 1) specialFeatures.push('noDeposit');
    if (filters.instantBooking === '1') specialFeatures.push('instantBooking');
    if (filters.superLister === '1') specialFeatures.push('superLister');
    if (filters.verified === '1') specialFeatures.push('verified');

    return specialFeatures.join(',');
  },
  poiFromFilters(filters) {
    if (filters.poiLat !== null && filters.poiLng !== null) {
      return `${filters.poiLat},${filters.poiLng}`;
    }

    return '';
  },
  setSearchQueryCustomDimensions(city, filters, resultsReturned) {
    try {
      const cd = {
        searchCity: city,
        searchMoveIn: filters.moveIn,
        searchMoveOut: filters.moveOut,
        searchFlexibleMoveIn: filters.flexibleMoveIn,
        searchFlexibleMoveOut: filters.flexibleMoveOut,
        searchPriceRange: filters.budget,
        searchPropertyTypes: filters.type.join(','),
        searchNumberBedrooms: filters.topology.join(','),
        searchSpecialFeatures: this.specialFeaturesFromFilters(filters),
        searchNeighborhoods: filters.areaId.join(','),
        searchBedTypes: filters.bed,
        searchNumberBathrooms: filters.bathrooms.join(','),
        searchPrivateBathroom: filters.bathrooms.join(','),
        searchFeatures: filters.features.join(','),
        searchPOI: this.poiFromFilters(filters),
        searchSortBy: filters.sortBy,
        searchPageNumber: filters.page,
        searchNumberResults: resultsReturned
      };

      this.setCustomDimensions(cd);
    } catch (err) {
      console.error('Failed to set search query custom dimensions', { err });
    }
  },
  setSearchMapCustomDimensions(
    northEastBoundary,
    southWestBoundary,
    numberResultsViewport
  ) {
    try {
      const cd = {
        searchMapNorthEastBoundary: northEastBoundary,
        searchMapSouthWestBoundary: southWestBoundary,
        searchNumberResultsViewport: numberResultsViewport
      };

      this.setCustomDimensions(cd);
    } catch (err) {
      console.error('Failed to set Search Map Custom Dimensions', {
        err
      });
    }
  },
  setSearchPropertyCustomDimensions(homecard, cityId) {
    try {
      const propertyCd = {
        propertyCity: cityId,
        propertyType: mappings.mapHomecardTypeToProductCategory(homecard.type),
        propertyChecked: homecard.checked.toString(),
        propertyRoomsNumber: Array.isArray(homecard.roomsInApartment)
          ? homecard.roomsInApartment.length
          : undefined,
        propertyFeatures: mappings.mapHouseFeatures(homecard.features)
      };
      this.setCustomDimensions(propertyCd);
    } catch (err) {
      console.error('Failed to track getPropertyCustomDimensions', {
        err
      });
    }
  }
});

export default {
  CustomDimensionsManager
};
